import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Label,
  TextPlain,
  TitleH2,
  TitleH3,
  TitleH4
} from "components/generic";
import { theme } from "styles/theme";
import { DialogWrapper, FlexBox, GridWrapper } from "components/containers";
import Range, { IRangeHook } from "components/Range";
import {
  HOST_PLAN_INTAKE_ROUTE,
  HOST_PROFILE_EDIT_FIRST_ROUTE,
  MONTHS,
  STUDENT_ROOMS_ROUTE
} from "utils/constants";

import { arrayLanguages } from "utils/fetchMapInfo";
import { RoleUser } from "interfaces/interfaces";
import { useContextHospi } from "context/ContextHospi";

import React, { useState } from "react";
import { IcoCalendar } from "components/InputCalendar/styles";
import { HostVerify } from "pages/ProfileUserEdit/HostVerified";
import Modal from "components/Modal";
import { TLang } from "assets/data/isoNameLanguage";
import { useHospiNavigate } from "../../../utils/common";

export const NoMatchesInfoSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matches");
  return (
    <>
      <TitleH3>{t("student.noMatches.text")}</TitleH3>
      <Button
        main
        h="40px"
        onClick={() =>
          navigate(`/${i18n.language}${STUDENT_ROOMS_ROUTE.slice(5)}`)
        }
      >
        {t("student.noMatches.btnText")}
      </Button>
    </>
  );
};

export const AboutMeView: React.FC<{
  genderUser?: string;
  studyStudent?: string;
  spokenLanguage?: string[];
  expirenceStudent?: string[];
  descriptionUser?: string;
}> = ({
  genderUser,
  studyStudent,
  spokenLanguage,
  expirenceStudent,
  descriptionUser
}) => {
  return (
    <GridWrapper gap="8px">
      <Label p="0">
        {genderUser
          ? genderUser[0].toUpperCase() +
            genderUser?.substring(1).replaceAll("_", " ")
          : ""}
      </Label>
      {studyStudent && (
        <Label p="0" style={{ wordBreak: "break-word" }}>
          {studyStudent || ""}
        </Label>
      )}
      {spokenLanguage && <SpokenLanguages items={spokenLanguage || ""} />}
      {expirenceStudent && (
        <ul>
          {expirenceStudent.map((item) => (
            <li key={item} style={{ marginTop: "4px" }}>
              <Label p="0">{item}</Label>
            </li>
          ))}
        </ul>
      )}
      <Label p="0" style={{ wordBreak: "break-word" }}>
        {descriptionUser || ""}
      </Label>
    </GridWrapper>
  );
};

export const SpokenLanguages: React.FC<{ items: string[] }> = ({ items }) => {
  const { i18n } = useTranslation();
  const curLang: TLang = (i18n.language.toUpperCase() as TLang) ?? "GB";
  return (
    <FlexBox direction="row" align="flex-start" justify="flex-start">
      {arrayLanguages(items, curLang).map((item) => (
        <Label
          bgColor={theme.colors.secondary}
          key={item}
          style={{ border: `1px solid ${theme.colors.border}` }}
          p="4px"
          m="4px 4px 0 0"
        >
          {item}
        </Label>
      ))}
    </FlexBox>
  );
};

export interface ISocialPreferences {
  roleUser: RoleUser;
  privacy: IRangeHook;
  newInTown: IRangeHook;
  muted?: boolean;
}

export const SocialPreferences: React.FC<ISocialPreferences> = ({
  roleUser,
  privacy,
  newInTown,
  muted = true
}) => {
  const { currentUserRole } = useContextHospi();
  const { t } = useTranslation(currentUserRole);
  return (
    <>
      <Range
        {...privacy}
        disabled={muted}
        title={t(`${roleUser}Profile.socialPreferences.form.privacy.title`)}
        markers={t(`${roleUser}Profile.socialPreferences.form.privacy.items`, {
          returnObjects: true
        })}
        p="0 0 24px 0"
        min={1}
        max={100}
      />
      <Range
        {...newInTown}
        disabled={muted}
        title={t(`${roleUser}Profile.socialPreferences.form.newInTown.title`)}
        markers={t(
          `${roleUser}Profile.socialPreferences.form.newInTown.items`,
          { returnObjects: true }
        )}
        min={1}
        max={100}
      />
    </>
  );
};

interface IDateLine {
  start: string;
  end: string;
  ico?: boolean;
  context?: string; // add this line
  c?: "hardGray";
}

export const DateLine: React.FC<
  IDateLine & React.HTMLAttributes<HTMLDivElement>
> = ({ start, end, ico, context = "createProfile", ...props }) => {
  const { t } = useTranslation();
  const [startY, startM, startD] = start.slice(0, 10).split("-");
  const [endY, endM, endD] = end.slice(0, 10).split("-");

  const startDate = `${startD} ${t(
    `calendar.months.${MONTHS[Number(startM) - 1]}`
  )
    .toLocaleLowerCase()
    .slice(0, 3)}
${startY}`;

  const lineDate = `${startDate} - ${endD} ${t(
    `calendar.months.${MONTHS[Number(endM) - 1]}`
  )
    .toLocaleLowerCase()
    .slice(0, 3)}
${endY}`;

  const defaultText =
    context === "profileOverview" ? t("noDatesSelected") : t("chooseDate");
  const endDateText =
    end === "1970-01-01" ? `${startDate} - ${t("noEndDate")}` : lineDate;

  return (
    <TitleH4 {...props}>
      {ico && (
        <IcoCalendar
          size="1em"
          fontColor
          style={{ marginRight: "0.5em", cursor: "default" }}
        />
      )}
      {start && end ? endDateText : defaultText}
    </TitleH4>
  );
};

export const VerifiedInfo: React.FC = () => {
  const { userProfile } = useContextHospi();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {userProfile?.user_type === "host" ? (
        <HostVerify
          verification_status={userProfile.verification_status}
          onClick={() => {
            if (userProfile.verification_status === "unverified") {
              setShowModal(true);
            }
          }}
        />
      ) : (
        <span />
      )}
      {showModal && (
        <HostVerificationModal
          show={true}
          onClose={() => {
            setShowModal(false);
          }}
          onValidationRequested={() => {
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export const HostVerificationModal = ({
  show,
  onClose,
  onValidationRequested
}: {
  show: boolean;
  onClose: () => void;
  onValidationRequested: () => void;
}) => {
  const { userProfile } = useContextHospi();
  const navigate = useHospiNavigate();
  const { t } = useTranslation("host");

  const isProfileComplete = userProfile!.is_profile_complete;

  return (
    <Modal isShow={show} handleClose={() => onClose()}>
      <DialogWrapper>
        <TitleH2>{t(`hostProfile.requestVerified.title`)}</TitleH2>
        <TextPlain
          style={{
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            marginTop: "24px"
          }}
        >
          {isProfileComplete
            ? t(`hostProfile.requestVerified.text`, {
                joinArrays: "\n\n"
              })
            : t(`hostProfile.requestVerified.message`, {
                joinArrays: "\n\n"
              })}
        </TextPlain>

        <div className="buttons">
          <Button normal onClick={() => onClose()}>
            {isProfileComplete
              ? t(`hostProfile.requestVerified.buttonSkip`)
              : t(`hostProfile.requestVerified.buttonCancel`)}
          </Button>
          {isProfileComplete ? (
            <Button
              main
              onClick={async () => {
                onValidationRequested();
                navigate(HOST_PLAN_INTAKE_ROUTE);
                onClose();
              }}
            >
              {t(`hostProfile.requestVerified.buttonRequest`)}
            </Button>
          ) : (
            <Button
              main
              onClick={() => {
                navigate(HOST_PROFILE_EDIT_FIRST_ROUTE);
                onClose();
              }}
            >
              {t(`hostProfile.requestVerified.buttonGoEditProfile`)}
            </Button>
          )}
        </div>
      </DialogWrapper>
    </Modal>
  );
};
